import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Bell, Lock, Shield, HelpCircle, Phone, Edit2, AlertTriangle, Send, Download, FileJson, Globe, Lightbulb, MessageCircle, Smartphone } from "lucide-react";

export function SettingsTab({
  notifications,
  updateNotificationPref,
  parent,
  consentStatus,
  formatDate,
  setShowChangePasswordModal,
  setShowWithdrawModal,
  setShowSupportModal,
  selectedChild,
  onDownloadData,
  onWithdrawChild,
  onRegrantChild,
  withdrawChildLoading,
  regrantLoading,
  languagePreference,
  onLanguageChange,
  supportedLanguages,
  creditAlerts,
  onCreditAlertChange,
  pushNotification
}) {
  return (
    <div className="space-y-6">
      {/* Language Preference - NEW */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-indigo-600" />
            WhatsApp Language
          </CardTitle>
          <CardDescription>
            Choose the language for WhatsApp lessons and messages for {selectedChild?.name || "your child"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg">
            <div>
              <p className="font-medium">Preferred Language</p>
              <p className="text-sm text-gray-500">All WhatsApp content will be in this language</p>
            </div>
            <Select
              value={languagePreference || "english"}
              onValueChange={onLanguageChange}
              disabled={!selectedChild}
            >
              <SelectTrigger className="w-[200px]" data-testid="language-select">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                {(supportedLanguages || [
                  { code: "english", name: "English" },
                  { code: "hindi", name: "हिंदी (Hindi)" },
                  { code: "punjabi", name: "ਪੰਜਾਬੀ (Punjabi)" },
                  { code: "tamil", name: "தமிழ் (Tamil)" },
                  { code: "marathi", name: "मराठी (Marathi)" },
                  { code: "telugu", name: "తెలుగు (Telugu)" },
                  { code: "kannada", name: "ಕನ್ನಡ (Kannada)" }
                ]).map(lang => (
                  <SelectItem key={lang.code} value={lang.code} data-testid={`lang-${lang.code}`}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-gray-500">
            This setting affects lessons, questions, feedback, and all AI-generated content on WhatsApp.
          </p>
        </CardContent>
      </Card>

      {/* Progress Email Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-emerald-600" />
            Progress Email Notifications
          </CardTitle>
          <CardDescription>Get updates about your child's learning progress</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Session Completion</p>
              <p className="text-sm text-gray-500">Get notified when your child finishes a learning session</p>
            </div>
            <Switch 
              checked={notifications.session_complete} 
              onCheckedChange={(v) => updateNotificationPref('session_complete', v)}
              data-testid="notif-session-complete"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Daily Goal Complete</p>
              <p className="text-sm text-gray-500">Celebrate when your child reaches their daily learning limit</p>
            </div>
            <Switch 
              checked={notifications.daily_limit} 
              onCheckedChange={(v) => updateNotificationPref('daily_limit', v)}
              data-testid="notif-daily-limit"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Weekly Progress Report</p>
              <p className="text-sm text-gray-500">Receive a detailed weekly summary every Monday</p>
            </div>
            <Switch 
              checked={notifications.weekly_report} 
              onCheckedChange={(v) => updateNotificationPref('weekly_report', v)}
              data-testid="notif-weekly-report"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Streak Milestones</p>
              <p className="text-sm text-gray-500">Get notified when your child achieves streak milestones (7, 14, 30 days)</p>
            </div>
            <Switch 
              checked={notifications.streak_milestones} 
              onCheckedChange={(v) => updateNotificationPref('streak_milestones', v)}
              data-testid="notif-streak-milestones"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Inactivity Reminders</p>
              <p className="text-sm text-gray-500">Gentle reminder if your child hasn't learned for 3+ days</p>
            </div>
            <Switch 
              checked={notifications.inactivity_reminder} 
              onCheckedChange={(v) => updateNotificationPref('inactivity_reminder', v)}
              data-testid="notif-inactivity-reminder"
            />
          </div>
        </CardContent>
      </Card>

      {/* General Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-600" />
            General Notifications
          </CardTitle>
          <CardDescription>Manage other notification channels</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Email Reminders</p>
              <p className="text-sm text-gray-500">Receive renewal and activity reminders via email</p>
            </div>
            <Switch 
              checked={notifications.email_reminders} 
              onCheckedChange={(v) => updateNotificationPref('email_reminders', v)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">WhatsApp Reminders</p>
              <p className="text-sm text-gray-500">Receive reminders via WhatsApp</p>
            </div>
            <Switch 
              checked={notifications.whatsapp_reminders} 
              onCheckedChange={(v) => updateNotificationPref('whatsapp_reminders', v)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Subscription Alerts</p>
              <p className="text-sm text-gray-500">Alerts about subscription expiry and renewal</p>
            </div>
            <Switch 
              checked={notifications.subscription_alerts} 
              onCheckedChange={(v) => updateNotificationPref('subscription_alerts', v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Homework Credit Alerts */}
      <Card className="border-purple-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-purple-600" />
            Homework Credit Alerts
          </CardTitle>
          <CardDescription>Get notified when your child&apos;s homework help credits run low</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-purple-50 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">WhatsApp Alerts</p>
                  <p className="text-sm text-gray-500">Get instant WhatsApp messages when credits are low</p>
                </div>
              </div>
              <Switch 
                checked={creditAlerts?.whatsapp_alerts ?? true}
                onCheckedChange={(v) => onCreditAlertChange?.('whatsapp_alerts', v)}
                data-testid="credit-alert-whatsapp"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium">Email Alerts</p>
                  <p className="text-sm text-gray-500">Receive email notifications for credit updates</p>
                </div>
              </div>
              <Switch 
                checked={creditAlerts?.email_alerts ?? true}
                onCheckedChange={(v) => onCreditAlertChange?.('email_alerts', v)}
                data-testid="credit-alert-email"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="font-medium">Browser Push Notifications</p>
                  <p className="text-sm text-gray-500">Get instant alerts even when Eduzy isn&apos;t open</p>
                </div>
              </div>
              {pushNotification?.isSupported ? (
                pushNotification.isSubscribed ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={pushNotification.unsubscribe}
                    disabled={pushNotification.loading}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="push-disable-btn"
                  >
                    {pushNotification.loading ? "..." : "Disable"}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={pushNotification.subscribe}
                    disabled={pushNotification.loading}
                    className="text-orange-600 border-orange-200 hover:bg-orange-50"
                    data-testid="push-enable-btn"
                  >
                    {pushNotification.loading ? "..." : "Enable"}
                  </Button>
                )
              ) : (
                <span className="text-xs text-gray-400">Not supported</span>
              )}
            </div>
            {pushNotification?.error && (
              <p className="text-xs text-red-500">{pushNotification.error}</p>
            )}
          </div>
          <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
            <p className="font-medium text-gray-700 mb-1">Alert Triggers:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>When credits fall below 5 (low balance warning)</li>
              <li>When credits fall below 2 (critical alert)</li>
              <li>When free trial is about to end (3 or fewer helps remaining)</li>
              <li>When credits expire in 7, 3, or 1 days</li>
              <li>When all credits are exhausted</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Password Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-blue-600" />
            Password Security
          </CardTitle>
          <CardDescription>Manage your account password</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Password</p>
              <p className="text-sm text-gray-500">Last changed: {parent?.password_changed_at ? formatDate(parent.password_changed_at) : "Never"}</p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowChangePasswordModal(true)}
              data-testid="change-password-btn"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            We recommend changing your password regularly for security.
          </p>
        </CardContent>
      </Card>

      {/* Per-Child Consent (DPDP) */}
      <Card data-testid="per-child-consent-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-600" />
            Per-Child Consent
          </CardTitle>
          <CardDescription>
            Withdraw or re-grant consent for an individual child. Other children are unaffected.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!selectedChild ? (
            <p className="text-sm text-gray-500">Select a child above to manage their consent.</p>
          ) : selectedChild.consent_status === "withdrawn" ? (
            <>
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                <div>
                  <p className="font-medium">{selectedChild.name}</p>
                  <p className="text-sm text-red-600">
                    Consent withdrawn{selectedChild.consent_withdrawn_at ? ` on ${formatDate(selectedChild.consent_withdrawn_at)}` : ""}.
                    {selectedChild.consent_purge_at ? ` Data will be permanently erased on ${formatDate(selectedChild.consent_purge_at)}.` : ""}
                  </p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs bg-red-100 text-red-700">Withdrawn</span>
              </div>
              <div className="pt-2">
                <Button
                  onClick={() => onRegrantChild && onRegrantChild(selectedChild)}
                  disabled={regrantLoading}
                  className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
                  data-testid="regrant-consent-btn"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  {regrantLoading ? "Restoring..." : "Re-grant Consent & Restore"}
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  Re-grant before the deletion date to restore {selectedChild.name}'s account and full history.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{selectedChild.name}</p>
                  <p className="text-sm text-gray-500">Consent active — learning enabled</p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs bg-green-100 text-green-700">Active</span>
              </div>
              <div className="pt-2">
                <Button
                  variant="destructive"
                  onClick={() => onWithdrawChild && onWithdrawChild(selectedChild)}
                  disabled={withdrawChildLoading}
                  className="w-full sm:w-auto"
                  data-testid="withdraw-child-consent-btn"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {withdrawChildLoading ? "Processing..." : "Withdraw Consent for this Child"}
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  Disables {selectedChild.name}'s access immediately. You have 30 days to re-grant consent before
                  their data is permanently erased (DPDP Act). Siblings are not affected.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Account Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            Account & Consent
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Your Email</p>
              <p className="text-sm text-gray-500">{parent?.email}</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Consent Status</p>
              <p className="text-sm text-gray-500">{consentStatus === 'active' ? 'Active' : 'Withdrawn'}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs ${consentStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {consentStatus}
            </span>
          </div>
          
          <div className="pt-4 border-t">
            <Button 
              variant="destructive" 
              onClick={() => setShowWithdrawModal(true)}
              className="w-full sm:w-auto"
              data-testid="withdraw-account-consent-btn"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Close Account & Delete All Children
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              This closes your entire account and permanently deletes <strong>all</strong> of your children and their data.
              To remove just one child, use the <strong>Per-Child Consent</strong> card above instead.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* DPDP Data Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileJson className="h-5 w-5 text-purple-600" />
            Download My Data (DPDP)
          </CardTitle>
          <CardDescription>
            Exercise your Right to Access under India's Digital Personal Data Protection Act
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-700">
              Under the DPDP Act 2023, you have the right to access all personal data we hold about your child. 
              This includes profile information, learning history, assessments, and more.
            </p>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Data Export</p>
              <p className="text-sm text-gray-500">
                Download all data for {selectedChild?.name || "your child"} in JSON format
              </p>
            </div>
            <Button 
              variant="outline"
              onClick={onDownloadData}
              disabled={!selectedChild}
              data-testid="download-data-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Data
            </Button>
          </div>
          
          <p className="text-xs text-gray-500">
            The export includes: Profile data, learning statistics, assessment history, practice tests, 
            study plans, certificates, support tickets, invoices, and audit trail.
          </p>
        </CardContent>
      </Card>

      {/* Support */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-blue-600" />
            Support
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <a href="mailto:info@contentiumtech.com" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <Mail className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-sm">Email</p>
                <p className="text-xs text-gray-500">info@contentiumtech.com</p>
              </div>
            </a>
            <a href="tel:+919654006470" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <Phone className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-sm">Phone</p>
                <p className="text-xs text-gray-500">+91 9654006470</p>
              </div>
            </a>
            <a href="/faq" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <HelpCircle className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-sm">FAQ</p>
                <p className="text-xs text-gray-500">Find answers</p>
              </div>
            </a>
          </div>
          <Button onClick={() => setShowSupportModal(true)} className="w-full" variant="outline">
            <Send className="h-4 w-4 mr-2" />
            Submit Support Request
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
